import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { messagesService } from '../lib/messages-service';
import { personalTodosService } from '../lib/personal-todos-service';
import { useFamilyContext } from '../contexts/FamilyContext';

export function useMessages(filters = {}) {
  const { family, currentUser } = useFamilyContext();
  
  // All hooks must be declared at the top level
  const [messages, setMessages] = useState([]);
  const [personalNotes, setPersonalNotes] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Use refs to track subscriptions and prevent memory leaks
  const subscriptionRef = useRef(null);
  const loadingRef = useRef(false);
  const mountedRef = useRef(true);
  
  // Properly memoize filters to prevent infinite loops
  const memoizedFilters = useMemo(() => {
    // Create a stable object with only defined properties
    const stabilizedFilters = {};
    if (filters.recipientId !== undefined) stabilizedFilters.recipientId = filters.recipientId;
    if (filters.senderId !== undefined) stabilizedFilters.senderId = filters.senderId;
    if (filters.type !== undefined) stabilizedFilters.type = filters.type;
    if (filters.priority !== undefined) stabilizedFilters.priority = filters.priority;
    if (filters.status !== undefined) stabilizedFilters.status = filters.status;
    if (filters.unreadOnly !== undefined) stabilizedFilters.unreadOnly = filters.unreadOnly;
    if (filters.parentMessageId !== undefined) stabilizedFilters.parentMessageId = filters.parentMessageId;
    if (filters.threadsOnly !== undefined) stabilizedFilters.threadsOnly = filters.threadsOnly;
    return stabilizedFilters;
  }, [
    filters?.recipientId,
    filters?.senderId,
    filters?.type,
    filters?.priority,
    filters?.status,
    filters?.unreadOnly,
    filters?.parentMessageId,
    filters?.threadsOnly
  ]);

  // Load messages with debouncing to prevent excessive API calls
  const loadMessages = useCallback(async (messageFilters = {}) => {
    if (!family?.id || !currentUser?.id || loadingRef.current) return;
    
    loadingRef.current = true;
    setLoading(true);
    try {
      const combinedFilters = { ...memoizedFilters, ...messageFilters };
      const data = await messagesService.getMessages(family.id, combinedFilters);
      
      // Only update state if component is still mounted
      if (mountedRef.current) {
        setMessages(data);
        setError(null);
      }
    } catch (err) {
      console.error('Error loading messages:', err);
      if (mountedRef.current) {
        setError(err.message);
      }
    } finally {
      loadingRef.current = false;
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [family?.id, currentUser?.id, memoizedFilters]);

  // Load personal notes with proper error handling
  const loadPersonalNotes = useCallback(async (noteFilters = {}) => {
    if (!currentUser?.id || loadingRef.current) return;
    
    try {
      const data = await messagesService.getPersonalNotes(currentUser.id, noteFilters);
      if (mountedRef.current) {
        setPersonalNotes(data);
      }
    } catch (err) {
      console.error('Error loading personal notes:', err);
      if (mountedRef.current) {
        setError(err.message);
      }
    }
  }, [currentUser?.id]);

  // Load unread count with debouncing
  const loadUnreadCount = useCallback(async () => {
    if (!family?.id || !currentUser?.id || loadingRef.current) return;
    
    try {
      const count = await messagesService.getUnreadCount(family.id, currentUser.id);
      if (mountedRef.current) {
        setUnreadCount(count);
      }
    } catch (err) {
      console.error('Error loading unread count:', err);
    }
  }, [family?.id, currentUser?.id]);

  // Message operations
  const sendMessage = useCallback(async (messageData) => {
    if (!currentUser?.id || !family?.id) {
      throw new Error('User not authenticated');
    }
    
    setLoading(true);
    try {
      const newMessage = await messagesService.sendMessage(family.id, messageData, currentUser.id);
      
      // Automatically create personal to-do items for recipients
      if (messageData.recipientIds && messageData.recipientIds.length > 0) {
        const todoPromises = messageData.recipientIds.map(async (recipientId) => {
          try {
            await personalTodosService.createTodo(recipientId, {
              title: `Note from ${currentUser.name || 'Family Member'}`,
              description: messageData.content,
              priority: messageData.priority || 'normal',
              dueDate: messageData.dueDate || null,
              sourceType: 'family_message',
              sourceId: newMessage.id,
              category: 'family_notes'
            });
          } catch (todoError) {
            console.warn(`Failed to create todo for recipient ${recipientId}:`, todoError);
          }
        });
        
        await Promise.allSettled(todoPromises);
      }
      
      setMessages(prev => [newMessage, ...prev]);
      setError(null);
      return newMessage;
    } catch (err) {
      console.error('Error sending message:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id, currentUser?.name, family?.id]);

  const replyToMessage = useCallback(async (parentMessageId, replyData) => {
    if (!currentUser?.id) {
      throw new Error('User not authenticated');
    }
    
    setLoading(true);
    try {
      const reply = await messagesService.replyToMessage(parentMessageId, replyData, currentUser.id);
      
      // Update the parent message in the local state to show it has replies
      setMessages(prev => prev.map(msg => 
        msg.id === parentMessageId 
          ? { ...msg, has_replies: true, thread_messages: [...(msg.thread_messages || []), reply] }
          : msg
      ));
      
      setError(null);
      return reply;
    } catch (err) {
      console.error('Error replying to message:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id]);

  const updateMessage = useCallback(async (messageId, updates) => {
    if (!currentUser?.id) {
      throw new Error('User not authenticated');
    }
    
    setLoading(true);
    try {
      const updatedMessage = await messagesService.updateMessage(messageId, updates, currentUser.id);
      setMessages(prev => prev.map(msg => 
        msg.id === messageId ? { ...msg, ...updatedMessage } : msg
      ));
      setError(null);
      return updatedMessage;
    } catch (err) {
      console.error('Error updating message:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id]);

  const deleteMessage = useCallback(async (messageId) => {
    if (!currentUser?.id) {
      throw new Error('User not authenticated');
    }
    
    setLoading(true);
    try {
      await messagesService.deleteMessage(messageId, currentUser.id);
      setMessages(prev => prev.filter(msg => msg.id !== messageId));
      setError(null);
    } catch (err) {
      console.error('Error deleting message:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id]);

  const broadcastMessage = useCallback(async (messageData, recipientIds = null) => {
    if (!currentUser?.id || !family?.id) {
      throw new Error('User not authenticated');
    }
    
    setLoading(true);
    try {
      const broadcast = await messagesService.broadcastMessage(family.id, messageData, currentUser.id, recipientIds);
      setMessages(prev => [broadcast, ...prev]);
      setError(null);
      return broadcast;
    } catch (err) {
      console.error('Error broadcasting message:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id, family?.id]);

  // Message status operations
  const markAsRead = useCallback(async (messageId) => {
    if (!currentUser?.id) return;
    
    try {
      await messagesService.markAsRead(messageId, currentUser.id);
      
      // Update local state
      setMessages(prev => prev.map(msg => {
        if (msg.id === messageId) {
          const updatedRecipients = msg.recipients?.map(r => 
            r.recipient_id === currentUser.id 
              ? { ...r, status: 'read', read_at: new Date().toISOString() }
              : r
          );
          return { ...msg, recipients: updatedRecipients };
        }
        return msg;
      }));
      
      // Update unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking message as read:', err);
    }
  }, [currentUser?.id]);

  const markAsAcknowledged = useCallback(async (messageId) => {
    if (!currentUser?.id) return;
    
    try {
      await messagesService.markAsAcknowledged(messageId, currentUser.id);
      
      // Update local state
      setMessages(prev => prev.map(msg => {
        if (msg.id === messageId) {
          const updatedRecipients = msg.recipients?.map(r => 
            r.recipient_id === currentUser.id 
              ? { ...r, status: 'acknowledged', acknowledged_at: new Date().toISOString() }
              : r
          );
          return { ...msg, recipients: updatedRecipients };
        }
        return msg;
      }));
    } catch (err) {
      console.error('Error acknowledging message:', err);
    }
  }, [currentUser?.id]);

  const markAsCompleted = useCallback(async (messageId) => {
    if (!currentUser?.id) return;
    
    try {
      const updatedMessage = await messagesService.markAsCompleted(messageId, currentUser.id);
      
      // Update local state
      setMessages(prev => prev.map(msg => 
        msg.id === messageId ? { ...msg, ...updatedMessage } : msg
      ));
    } catch (err) {
      console.error('Error marking message as completed:', err);
    }
  }, [currentUser?.id]);

  const archiveMessage = useCallback(async (messageId) => {
    if (!currentUser?.id) return;
    
    try {
      await messagesService.archiveMessage(messageId, currentUser.id);
      
      // Remove from local state if showing active messages
      if (!filters.status || filters.status !== 'archived') {
        setMessages(prev => prev.filter(msg => msg.id !== messageId));
      }
    } catch (err) {
      console.error('Error archiving message:', err);
    }
  }, [currentUser?.id, filters.status]);

  // Bulk operations
  const markMultipleAsRead = useCallback(async (messageIds) => {
    if (!currentUser?.id || !messageIds.length) return;
    
    try {
      await messagesService.markMultipleAsRead(messageIds, currentUser.id);
      
      // Update local state
      setMessages(prev => prev.map(msg => {
        if (messageIds.includes(msg.id)) {
          const updatedRecipients = msg.recipients?.map(r => 
            r.recipient_id === currentUser.id 
              ? { ...r, status: 'read', read_at: new Date().toISOString() }
              : r
          );
          return { ...msg, recipients: updatedRecipients };
        }
        return msg;
      }));
      
      // Update unread count
      setUnreadCount(prev => Math.max(0, prev - messageIds.length));
    } catch (err) {
      console.error('Error marking multiple messages as read:', err);
    }
  }, [currentUser?.id]);

  const deleteMultipleMessages = useCallback(async (messageIds) => {
    if (!currentUser?.id || !messageIds.length) return;
    
    try {
      await messagesService.deleteMultipleMessages(messageIds, currentUser.id);
      setMessages(prev => prev.filter(msg => !messageIds.includes(msg.id)));
    } catch (err) {
      console.error('Error deleting multiple messages:', err);
    }
  }, [currentUser?.id]);


  // Search functionality
  const searchMessages = useCallback(async (searchTerm, searchFilters = {}) => {
    if (!family?.id || !currentUser?.id || !searchTerm.trim()) return [];
    
    try {
      const results = await messagesService.searchMessages(family.id, searchTerm, currentUser.id, searchFilters);
      return results;
    } catch (err) {
      console.error('Error searching messages:', err);
      setError(err.message);
      return [];
    }
  }, [family?.id, currentUser?.id]);

  // Utility functions
  const getMessageTemplates = useCallback(() => {
    if (!currentUser?.role) return [];
    return messagesService.getMessageTemplates(currentUser.role);
  }, [currentUser?.role]);

  const getUnreadMessages = useCallback(() => {
    return messages.filter(msg => 
      msg.recipients?.some(r => 
        r.recipient_id === currentUser?.id && r.status === 'unread'
      )
    );
  }, [messages, currentUser?.id]);

  const getMessagesByType = useCallback((type) => {
    return messages.filter(msg => msg.type === type);
  }, [messages]);

  const getMessagesByPriority = useCallback((priority) => {
    return messages.filter(msg => msg.priority === priority);
  }, [messages]);

  // Load initial data with proper cleanup
  useEffect(() => {
    mountedRef.current = true;
    
    if (family?.id && currentUser?.id) {
      loadMessages();
      loadUnreadCount();
    }
    
    return () => {
      mountedRef.current = false;
    };
  }, [family?.id, currentUser?.id, loadMessages, loadUnreadCount]);

  // Real-time subscriptions with proper cleanup and debouncing
  useEffect(() => {
    if (!family?.id || !currentUser?.id) return;

    // Cleanup previous subscription
    if (subscriptionRef.current) {
      messagesService.unsubscribe(subscriptionRef.current);
      subscriptionRef.current = null;
    }

    // Create new subscription with debounced updates
    let updateTimeout;
    const debouncedUpdate = () => {
      clearTimeout(updateTimeout);
      updateTimeout = setTimeout(() => {
        if (mountedRef.current) {
          loadMessages();
          loadUnreadCount();
        }
      }, 500); // 500ms debounce
    };

    subscriptionRef.current = messagesService.subscribeToMessages(
      family.id,
      currentUser.id,
      (payload) => {
        console.log('🔔 Real-time message update:', payload);
        
        // Handle specific events optimistically
        if (payload.eventType === 'INSERT' && payload.new) {
          const newMessage = payload.new;
          
          // Only update if this message is relevant to current filters
          const isRelevant = (
            !memoizedFilters.recipientId || 
            newMessage.sender_id === currentUser.id ||
            payload.new.recipients?.some(r => r.recipient_id === currentUser.id)
          );
          
          if (isRelevant && mountedRef.current) {
            // Update unread count immediately for new messages
            if (newMessage.sender_id !== currentUser.id) {
              setUnreadCount(prev => prev + 1);
            }
            
            // Debounce the full reload
            debouncedUpdate();
          }
        } else {
          // For other events, just debounce the update
          debouncedUpdate();
        }
      }
    );

    return () => {
      clearTimeout(updateTimeout);
      if (subscriptionRef.current) {
        messagesService.unsubscribe(subscriptionRef.current);
        subscriptionRef.current = null;
      }
    };
  }, [family?.id, currentUser?.id, memoizedFilters, loadMessages, loadUnreadCount]);

  return {
    // Data
    messages,
    personalNotes,
    unreadCount,
    loading,
    error,

    // Message operations
    sendMessage,
    replyToMessage,
    updateMessage,
    deleteMessage,
    broadcastMessage,

    // Status operations
    markAsRead,
    markAsAcknowledged,
    markAsCompleted,
    archiveMessage,

    // Bulk operations
    markMultipleAsRead,
    deleteMultipleMessages,

    // Personal notes (re-enabled with proper handling)
    createPersonalNote: useCallback(async (noteData) => {
      if (!currentUser?.id) {
        throw new Error('User not authenticated');
      }
      
      try {
        const newNote = await messagesService.createPersonalNote(currentUser.id, noteData);
        if (mountedRef.current) {
          setPersonalNotes(prev => [newNote, ...prev]);
        }
        return newNote;
      } catch (err) {
        console.error('Error creating personal note:', err);
        throw err;
      }
    }, [currentUser?.id]),
    
    updatePersonalNote: useCallback(async (noteId, updates) => {
      if (!currentUser?.id) {
        throw new Error('User not authenticated');
      }
      
      try {
        const updatedNote = await messagesService.updatePersonalNote(noteId, updates, currentUser.id);
        if (mountedRef.current) {
          setPersonalNotes(prev => prev.map(note => 
            note.id === noteId ? { ...note, ...updatedNote } : note
          ));
        }
        return updatedNote;
      } catch (err) {
        console.error('Error updating personal note:', err);
        throw err;
      }
    }, [currentUser?.id]),
    
    deletePersonalNote: useCallback(async (noteId) => {
      if (!currentUser?.id) {
        throw new Error('User not authenticated');
      }
      
      try {
        await messagesService.deletePersonalNote(noteId, currentUser.id);
        if (mountedRef.current) {
          setPersonalNotes(prev => prev.filter(note => note.id !== noteId));
        }
      } catch (err) {
        console.error('Error deleting personal note:', err);
        throw err;
      }
    }, [currentUser?.id]),

    // Search and filtering
    searchMessages,
    getUnreadMessages,
    getMessagesByType,
    getMessagesByPriority,

    // Utility functions
    getMessageTemplates,

    // Data refresh with debouncing
    refreshMessages: loadMessages,
    refreshNotes: loadPersonalNotes,
    refreshUnreadCount: loadUnreadCount,
    refreshAll: useCallback(() => {
      if (!loadingRef.current) {
        loadMessages();
        loadPersonalNotes();
        loadUnreadCount();
      }
    }, [loadMessages, loadPersonalNotes, loadUnreadCount])
  };
}

// Specialized hook for a single message thread
export function useMessageThread(messageId) {
  const { family, currentUser } = useFamilyContext();
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadMessage = useCallback(async () => {
    if (!messageId || !currentUser?.id) return;
    
    setLoading(true);
    try {
      const data = await messagesService.getMessage(messageId, currentUser.id);
      setMessage(data);
      setError(null);
    } catch (err) {
      console.error('Error loading message thread:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [messageId, currentUser?.id]);

  const replyToThread = useCallback(async (replyData) => {
    if (!currentUser?.id || !messageId) {
      throw new Error('User not authenticated or message not found');
    }
    
    try {
      const reply = await messagesService.replyToMessage(messageId, replyData, currentUser.id);
      
      // Add reply to the thread
      setMessage(prev => ({
        ...prev,
        thread_messages: [...(prev?.thread_messages || []), reply],
        has_replies: true
      }));
      
      return reply;
    } catch (err) {
      console.error('Error replying to thread:', err);
      throw err;
    }
  }, [currentUser?.id, messageId]);

  useEffect(() => {
    loadMessage();
  }, [loadMessage]);

  return {
    message,
    loading,
    error,
    replyToThread,
    refreshMessage: loadMessage
  };
}

// Hook for message notifications
export function useMessageNotifications() {
  const { family, currentUser } = useFamilyContext();
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    if (!family?.id || !currentUser?.id) return;

    const subscription = messagesService.subscribeToMessages(
      family.id,
      currentUser.id,
      (payload) => {
        if (payload.eventType === 'INSERT' && payload.new.sender_id !== currentUser.id) {
          // New message from someone else
          const notification = {
            id: payload.new.id,
            type: 'new_message',
            title: 'New Message',
            message: `${payload.new.sender_name}: ${payload.new.subject || 'New message'}`,
            timestamp: new Date(),
            priority: payload.new.priority,
            messageId: payload.new.id
          };
          
          setNotifications(prev => [notification, ...prev].slice(0, 10)); // Keep last 10
          
          // Show browser notification if permitted
          if (Notification.permission === 'granted') {
            new Notification(notification.title, {
              body: notification.message,
              icon: '/family-icon.png'
            });
          }
        }
      }
    );

    return () => messagesService.unsubscribe(subscription);
  }, [family?.id, currentUser?.id]);

  const clearNotification = useCallback((notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  return {
    notifications,
    clearNotification,
    clearAllNotifications
  };
}